/**
 * useZoomControls Hook
 *
 * Shared hook for map zoom button state and handlers.
 * Tracks zoom level via map 'zoom' event and disables
 * zoom-in/zoom-out buttons when at min/max zoom.
 *
 * @module hooks/useZoomControls
 */

import { useState, useEffect, useCallback, type RefObject } from 'react';
import type { MapRef } from '@vis.gl/react-maplibre';

import { ZOOM_PRECISION_THRESHOLD } from '@/constants';
import { MAP_CONFIG } from '@/config/maplibre';

interface UseZoomControlsProps {
  mapRef?: RefObject<MapRef | null>;
  isMapReady?: boolean;
  /** Lower zoom bound for the disabled state. Defaults to MAP_CONFIG.minZoom. */
  minZoom?: number;
  /** Upper zoom bound for the disabled state. Defaults to MAP_CONFIG.maxZoom. */
  maxZoom?: number;
}

interface UseZoomControlsReturn {
  isZoomInDisabled: boolean;
  isZoomOutDisabled: boolean;
  handleZoomIn: () => void;
  handleZoomOut: () => void;
}

/**
 * Provides zoom-in/zoom-out handlers and disabled state
 * based on the current map zoom level.
 *
 * Subscribes to map 'zoom' and 'zoomend' events to keep
 * button disabled states in sync with the actual zoom.
 */
export function useZoomControls({
  mapRef,
  isMapReady = false,
  minZoom = MAP_CONFIG.minZoom,
  maxZoom = MAP_CONFIG.maxZoom,
}: UseZoomControlsProps): UseZoomControlsReturn {
  const [isZoomInDisabled, setIsZoomInDisabled] = useState(true);
  const [isZoomOutDisabled, setIsZoomOutDisabled] = useState(true);

  const handleZoomIn = useCallback(() => {
    if (!mapRef?.current || isZoomInDisabled) return;
    mapRef.current.zoomIn();
  }, [mapRef, isZoomInDisabled]);

  const handleZoomOut = useCallback(() => {
    if (!mapRef?.current || isZoomOutDisabled) return;
    mapRef.current.zoomOut();
  }, [mapRef, isZoomOutDisabled]);

  useEffect(() => {
    const map = mapRef?.current;
    if (!map || !isMapReady) return;

    const updateZoomState = () => {
      const zoom = map.getZoom();
      setIsZoomInDisabled(zoom >= maxZoom - ZOOM_PRECISION_THRESHOLD);
      setIsZoomOutDisabled(zoom <= minZoom + ZOOM_PRECISION_THRESHOLD);
    };

    updateZoomState();
    map.on('zoomend', updateZoomState);

    return () => {
      map.off('zoomend', updateZoomState);
    };
  }, [mapRef, isMapReady, minZoom, maxZoom]);

  return { isZoomInDisabled, isZoomOutDisabled, handleZoomIn, handleZoomOut };
}
